import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { useExternalAccessRights, useRetailContext, useVenuePosIntegrationStatus } from 'common/hooks';
import { isRetailVenue } from 'common/utils';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import useBestOfWoltFeatureFlag from 'modules/best-of-wolt/hooks/useBestOfWoltFeatureFlag';

import { AvailableRouteItem } from './types';
import { useVenueContext } from '../venue/VenueContext';

type Params = {
  feature?: string;
  slug?: string;
};

export const useVenueAvailableRoutes = () => {
  const venue = useVenueContext();
  const { slug: slugOrId = venue.slug } = useParams<Params>();
  const intl = useIntl();
  const isRetailContext = useRetailContext();
  const { data: venuePosIntegrationStatus } = useVenuePosIntegrationStatus(venue.id);
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const isDaasVenue = venue.type === 'daas';

  const { isBestOfWoltEnabled } = useBestOfWoltFeatureFlag({
    venueId: venue.id,
    venueCountry: venue.country,
    venueCity: venue.city,
  });
  const items: AvailableRouteItem[] = useMemo(() => {
    const t = (id: string) => intl.formatMessage({ id });
    const insightsSubItems = [
      {
        label: t('insights.performance.header'),
        link: `/venue/${slugOrId}/insights/performance`,
        type: 'INSIGHTS_V2_PERFORMANCE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_PERFORMANCE' as const,
      },
      {
        label: t('insights.operations.header'),
        link: `/venue/${slugOrId}/insights/operations`,
        type: 'INSIGHTS_V2_OPERATIONS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_OPERATIONS' as const,
      },
      {
        label: t('insights.customer-base.header'),
        link: `/venue/${slugOrId}/insights/customer-base`,
        type: 'INSIGHTS_V2_CUSTOMER_BASE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_CUSTOMER_BASE' as const,
      },
      {
        label: t('insights.menu.header'),
        link: `/venue/${slugOrId}/insights/menu`,
        type: 'INSIGHTS_V2_MENU' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_MENU' as const,
      },
      {
        label: t('insights.ratings-and-reviews.header'),
        link: `/venue/${slugOrId}/insights/ratings`,
        type: 'INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
      },
    ];
    return [
      ...(permissionUtils.hasVenueInsightsPermission(permissions, externalAccessRights, venue) && !isDaasVenue
        ? [
            {
              label: t('navigation.insights'),
              link: `/venue/${slugOrId}/insights`,
              type: 'INSIGHTS_V2' as const,
              trackingKey: 'OPEN_INSIGHTS_V2' as const,
              subItems: insightsSubItems,
            },
          ]
        : []),
      ...(permissionUtils.hasVenueInsightsPermission(permissions, externalAccessRights, venue) &&
      !isDaasVenue &&
      isBestOfWoltEnabled
        ? [
            {
              label: t('best-of-wolt.title'),
              link: `/venue/${slugOrId}/best-of-wolt`,
              type: 'BEST_OF_WOLT' as const,
              trackingKey: 'OPEN_BEST_OF_WOLT' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasPayoutReportsPermission(permissions, externalAccessRights, venue) && !isDaasVenue
        ? [
            {
              label: t('navigation.payout-reports'),
              link: `/venue/${slugOrId}/payouts`,
              type: 'PAYOUT_REPORTS' as const,
              trackingKey: 'OPEN_PAYOUT_REPORTS' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasPurchasesPermission(permissions, externalAccessRights, venue)
        ? [
            {
              label: t('navigation.purchases'),
              link: `/venue/${slugOrId}/purchases`,
              type: 'PURCHASES' as const,
              trackingKey: 'OPEN_PURCHASES' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasSettlementsPermission(permissions, externalAccessRights, venue)
        ? [
            {
              label: t('navigation.settlements'),
              link: `/venue/${slugOrId}/additions-and-deductions`,
              type: 'SETTLEMENTS' as const,
              trackingKey: 'OPEN_SETTLEMENTS' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasMenuEditorPermission(permissions, externalAccessRights, venue) && !isDaasVenue
        ? [
            {
              label: t('listing-manager.title'),
              link: `/venue/${slugOrId}/menu-editor`,
              type: isRetailVenue(venue) ? ('MENU_EDITOR_RETAIL' as const) : ('MENU_EDITOR_RESTAURANT' as const),
              trackingKey: 'OPEN_MENU_EDITOR' as const,
            },
          ]
        : []),
      {
        label: t('campaigns.title'),
        link: `/venue/${slugOrId}/campaigns`,
        type: 'CAMPAIGNS' as const,
        trackingKey: 'OPEN_CAMPAIGNS' as const,
      },
      ...(permissionUtils.hasPickingRoutePermission(permissions, externalAccessRights, venue) &&
      isRetailContext &&
      !isDaasVenue
        ? [
            {
              label: t('navigation.picking-route'),
              link: `/venue/${slugOrId}/picking-route`,
              type: 'PICKING_ROUTE' as const,
              trackingKey: 'OPEN_PICKING_ROUTE' as const,
            },
          ]
        : []),

      ...(permissionUtils.hasVenueDetailsViewPermission(permissions, externalAccessRights, venue)
        ? [
            {
              label: isRetailContext ? t('navigation.venue-details-retail') : t('navigation.venue-details'),
              link: `/venue/${slugOrId}/details`,
              type: 'VENUE_DETAILS' as const,
              trackingKey: 'OPEN_LIVE_PURCHASES' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasTranslationPermission(permissions, externalAccessRights, venue) &&
      permissionUtils.hasMenuContentEditPermission(
        permissions,
        externalAccessRights,
        venue,
        venuePosIntegrationStatus,
      ) &&
      !isDaasVenue
        ? [
            {
              label: t('navigation.translation'),
              link: `/venue/${slugOrId}/translation`,
              type: 'TRANSLATION' as const,
              trackingKey: 'OPEN_TRANSLATION' as const,
            },
          ]
        : []),
      ...(permissionUtils.hasVenueUserManagementPermission(permissions, externalAccessRights, venue)
        ? [
            {
              label: t('navigation.access-management'),
              link: `/venue/${slugOrId}/access`,
              type: 'ACCESS_MANAGEMENT' as const,
              trackingKey: 'OPEN_ACCESS_MANAGEMENT' as const,
            },
          ]
        : []),
    ];
  }, [
    slugOrId,
    permissions,
    externalAccessRights,
    venue,
    isDaasVenue,
    isBestOfWoltEnabled,
    isRetailContext,
    venuePosIntegrationStatus,
    intl,
  ]);

  return { items };
};
